/**
 * Visage SYNTHÉTIQUE (tests e2e, démo) : le faux flux caméra de Chrome ne
 * contient pas de visage. Points = modèle canonique MediaPipe (Apache 2.0),
 * animé ; expressions pilotables (neutre, rire, clignement, absent).
 */
import canonical from '../ar/fixtures/canonicalFace.json';
import { idealFace, SMILEY_IDS, type SmileyId } from '../smiley/smileys';
import type { FaceFrameListener, FaceSource } from './faceSource';
import type { Blendshapes, Point3 } from './types';

const NEUTRAL: Blendshapes = {
  mouthSmileLeft: 0.03,
  mouthSmileRight: 0.03,
  cheekSquintLeft: 0.02,
  cheekSquintRight: 0.02,
  jawOpen: 0.03,
  eyeBlinkLeft: 0.05,
  eyeBlinkRight: 0.05,
};

/** Visage de référence MediaPipe (de face), qui oscille doucement. */
export function syntheticLandmarks(t: number): Point3[] {
  const phase = (t / 1000) * 2 * Math.PI * 0.5;
  const dx = 0.03 * Math.sin(phase);
  const roll = 0.08 * Math.sin(phase * 0.7);
  const cos = Math.cos(roll);
  const sin = Math.sin(roll);
  return (canonical.points as Point3[]).map((p) => {
    const x = p.x - 0.5;
    const y = p.y - 0.5;
    return { x: 0.5 + dx + x * cos - y * sin, y: 0.5 + x * sin + y * cos, z: p.z };
  });
}

export type SyntheticExpression = 'neutral' | 'laugh' | 'blink' | 'none' | SmileyId;

const EXPRESSIONS: Record<Exclude<SyntheticExpression, 'none'>, Blendshapes> = {
  neutral: NEUTRAL,
  laugh: {
    ...NEUTRAL,
    mouthSmileLeft: 0.85,
    mouthSmileRight: 0.85,
    cheekSquintLeft: 0.6,
    cheekSquintRight: 0.6,
    jawOpen: 0.5,
  },
  blink: { ...NEUTRAL, eyeBlinkLeft: 0.9, eyeBlinkRight: 0.9 },
  // Imite ce smiley : chaque smiley parfaitement imité.
  ...(Object.fromEntries(SMILEY_IDS.map((id) => [id, idealFace(id, NEUTRAL)])) as Record<
    SmileyId,
    Blendshapes
  >),
};

export interface SyntheticFaceSource extends FaceSource {
  /** Pilotage (tests e2e) : expression émise à partir de la prochaine frame. */
  setExpression(expression: SyntheticExpression): void;
}

export function createSyntheticFaceSource({
  fps = 30,
  now = () => performance.now(),
}: { fps?: number; now?: () => number } = {}): SyntheticFaceSource {
  const listeners = new Set<FaceFrameListener>();
  let timer: ReturnType<typeof setInterval> | undefined;
  let expression: SyntheticExpression = 'neutral';
  return {
    start() {
      timer ??= setInterval(() => {
        const t = now();
        const frame =
          expression === 'none'
            ? { t, blendshapes: null }
            : { t, blendshapes: EXPRESSIONS[expression], landmarks: syntheticLandmarks(t) };
        for (const l of listeners) l(frame);
      }, 1000 / fps);
      return Promise.resolve();
    },
    stop() {
      clearInterval(timer);
      timer = undefined;
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    setExpression(e) {
      expression = e;
    },
  };
}
